import { useState, useRef, useCallback, useEffect } from 'react'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

/**
 * Voice dictation hook using Web Speech API (free, browser-native).
 * Optional Whisper path: set useWhisper=true to record audio and POST to /osa/transcribe.
 *
 * Returns:
 *   isSupported    — false if browser doesn't support SpeechRecognition
 *   isListening    — true while mic is active
 *   transcript     — final committed transcript text
 *   interimText    — live interim text (updates continuously)
 *   startListening — fn() to start
 *   stopListening  — fn() to stop and commit
 *   reset          — fn() to clear transcript
 */
export function useVoiceDictation({ onFinalTranscript, useWhisper = false, whisperUploadFn = null } = {}) {
  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interimText, setInterimText] = useState('')
  const recognitionRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])

  const isSupported = !!SpeechRecognition

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    setIsListening(false)
    setInterimText('')
  }, [])

  const startListening = useCallback(async () => {
    if (isListening) return
    setInterimText('')

    if (useWhisper && whisperUploadFn) {
      // Whisper path: record audio, upload on stop
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        audioChunksRef.current = []
        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
        recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data) }
        recorder.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop())
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
          try {
            const result = await whisperUploadFn(blob)
            setTranscript((prev) => (prev ? prev + ' ' + result.text : result.text))
            if (onFinalTranscript) onFinalTranscript(result.text)
          } catch (e) {
            console.error('Whisper upload failed:', e)
          }
        }
        recorder.start()
        mediaRecorderRef.current = recorder
        setIsListening(true)
      } catch (e) {
        console.error('Mic access denied:', e)
      }
      return
    }

    // Web Speech API path
    if (!SpeechRecognition) return
    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event) => {
      let interim = ''
      let final = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          final += text + ' '
        } else {
          interim += text
        }
      }
      if (final) {
        setTranscript((prev) => (prev ? prev + ' ' + final.trim() : final.trim()))
        if (onFinalTranscript) onFinalTranscript(final.trim())
      }
      setInterimText(interim)
    }

    recognition.onerror = (e) => {
      if (e.error !== 'no-speech') console.error('Speech recognition error:', e.error)
    }

    recognition.onend = () => {
      setIsListening(false)
      setInterimText('')
    }

    recognitionRef.current = recognition
    recognition.start()
    setIsListening(true)
  }, [isListening, useWhisper, whisperUploadFn, onFinalTranscript])

  const reset = useCallback(() => {
    setTranscript('')
    setInterimText('')
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop()
      if (mediaRecorderRef.current?.state !== 'inactive') {
        mediaRecorderRef.current?.stop()
      }
    }
  }, [])

  return { isSupported, isListening, transcript, setTranscript, interimText, startListening, stopListening, reset }
}
